import { useState } from "react";
import { Lock, TrendingUp } from "lucide-react";

import type { CycleResponse } from "../../api/client";
import {
  CycleStartError,
  CycleStopError,
  type Bias,
} from "../../hooks/useActiveCycle";
import type { AutoModeState } from "../../hooks/useAutoMode";
import type { OperatorEntryState } from "../../hooks/useOperatorEntry";
import type { BreakerEntry } from "../../hooks/useBreakerState";
import { cn } from "@/lib/utils";
import { ToneBadge } from "@/components/common/SemanticBadge";
import AutoModeCard from "./AutoModeCard";
import BiasSelector from "./BiasSelector";

interface CockpitCommandProps {
  marketStatus: string | undefined;
  breakers: BreakerEntry[];
  wsAvailable: boolean;
  hasObserved: boolean;
  pending: boolean;
  onStart: (bias: Bias) => Promise<CycleResponse>;
  onStop: () => Promise<CycleResponse>;
  auto: AutoModeState;
  /** « Acheter maintenant » hors cycle (2026-10-22) — relayé à AutoModeCard. */
  operatorEntry?: OperatorEntryState;
}

/** marketStatus → titre + sous-titre (PROTECTED_LADDER_V6 §6 — seul TRADEABLE est « ouvert »). */
const MARKET_META: Record<string, { title: string; sub: string }> = {
  TRADEABLE: { title: "Marché ouvert", sub: "Négociable — tu peux lancer un cycle." },
  CLOSED: { title: "Marché fermé", sub: "Hors horaires / week-end / férié — lancement désactivé." },
  EDITS_ONLY: { title: "Clôtures seules", sub: "Fermer est permis, aucune entrée — lancement désactivé." },
  ON_AUCTION: { title: "Enchères", sub: "Enchères de pré-ouverture — lancement désactivé." },
  ON_AUCTION_NO_EDITS: { title: "Enchères (verrouillé)", sub: "Enchères, aucune modification — lancement désactivé." },
  SUSPENDED: { title: "Suspendu", sub: "Négociation suspendue — lancement désactivé." },
  OFFLINE: { title: "Hors ligne", sub: "Marché hors ligne — lancement désactivé." },
  UNKNOWN: { title: "Statut marché inconnu", sub: "Pas de signal IG — repli sur le calendrier des horaires." },
};

function humanStartError(reason: string): string {
  if (reason.includes("AUTO_ARMED")) return "AUTO est armé — coupe-le pour lancer un cycle manuel.";
  if (reason.includes("ACTIVE_CYCLE_EXISTS")) return "Le serveur a déjà un cycle actif. Nettoie-le d'abord.";
  if (reason.includes("SYSTEM_NOT_HEALTHY")) return "Le bot n'est pas HEALTHY — les démarrages sont bloqués.";
  if (reason.includes("BALANCE_BELOW_MIN")) return "Le solde du compte est sous le minimum de 1 000 €.";
  if (reason.includes("MARKET_NOT_OPEN")) return "Le marché DAX est fermé.";
  return `Refusé : ${reason}`;
}

/**
 * PROTECTED_LADDER_V6 launch cockpit. AUTO is a MODE (a switch), not a peer of the
 * BUY/SELL actions: flip AUTO on and the bot opens BUY (long, champion) cycles
 * automatically, disarming itself the moment a cycle closes at a loss (then emails
 * you). While AUTO is on, the manual BUY/SELL are locked out. Arming is
 * double-confirmed: typed phrase "ARM AUTO" + emailed OTP.
 */
export default function CockpitCommand({
  marketStatus,
  breakers,
  wsAvailable,
  hasObserved,
  pending,
  onStart,
  onStop,
  auto,
  operatorEntry,
}: CockpitCommandProps) {
  const [error, setError] = useState<string | null>(null);

  const status = marketStatus ?? "UNKNOWN";
  const open = status === "TRADEABLE";
  const meta = MARKET_META[status] ?? MARKET_META.UNKNOWN;
  const tripped = breakers.filter((b) => b.status === "tripped");

  const gates = [
    { key: "stream", label: "Flux", ok: wsAvailable, detail: wsAvailable ? "connecté" : "déconnecté" },
    { key: "market", label: "Marché", ok: open, detail: open ? "négociable" : status },
    {
      key: "breakers",
      label: "Breakers",
      ok: tripped.length === 0,
      detail: tripped.length === 0 ? "OK" : `${tripped.length} déclenché${tripped.length > 1 ? "s" : ""}`,
    },
  ];
  const ready = gates.every((g) => g.ok);
  const armed = auto.status?.armed ?? false;

  const start = async (bias: Bias) => {
    setError(null);
    try {
      await onStart(bias);
    } catch (e) {
      setError(e instanceof CycleStartError ? humanStartError(e.reason) : "Impossible de démarrer le cycle.");
    }
  };

  const clear = async () => {
    setError(null);
    try {
      await onStop();
    } catch (e) {
      setError(e instanceof CycleStopError ? "Aucun cycle actif à nettoyer." : "Impossible de nettoyer le cycle.");
    }
  };

  return (
    <section
      className="cockpit-command bg-card shadow-elevate relative flex flex-col overflow-hidden rounded-2xl border border-border"
      aria-label="Launch control"
      data-testid="cockpit-command"
      data-market-open={open}
      data-ready={ready}
      data-auto-armed={armed}
    >
      {/* ── marketStatus hero ── */}
      <div className="border-border flex flex-wrap items-center gap-4 border-b px-6 py-5">
        <div
          className={cn(
            "grid size-12 shrink-0 place-items-center rounded-xl border",
            open ? "border-buy/40 bg-buy-soft text-buy" : "border-warning/40 bg-warning-soft text-warning",
          )}
        >
          {open ? <TrendingUp className="size-6" aria-hidden /> : <Lock className="size-6" aria-hidden />}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className="text-foreground text-lg font-semibold">{meta.title}</span>
            <ToneBadge tone={open ? "buy" : "warning"} dot>
              {status}
            </ToneBadge>
          </div>
          <span className="text-muted-foreground text-sm">{meta.sub}</span>
        </div>
      </div>

      <div className="flex flex-col gap-4 px-6 py-5">
        {/* ── AUTO mode (the switch) ── */}
        <AutoModeCard auto={auto} armed={armed} operatorEntry={operatorEntry} />

        {/* ── manual launch (locked while AUTO is on) ── */}
        <ManualLaunch armed={armed} ready={ready} pending={pending} onStart={start} />

        {!hasObserved && (
          <button
            type="button"
            onClick={clear}
            disabled={pending}
            className="text-muted-foreground hover:text-foreground text-left text-xs underline-offset-2 hover:underline disabled:opacity-50"
          >
            État du cycle serveur inconnu — clique pour nettoyer un cycle qui tournerait déjà côté serveur.
          </button>
        )}

        {error && (
          <p className="text-loss text-sm" role="alert">
            {error}
          </p>
        )}

        {/* gate strip */}
        <div className="grid grid-cols-3 gap-2">
          {gates.map((g) => (
            <div
              key={g.key}
              className={cn(
                "flex flex-col gap-0.5 rounded-lg border px-3 py-2",
                g.ok ? "border-buy/30 bg-buy-soft" : "border-warning/30 bg-warning-soft",
              )}
            >
              <span className="text-muted-foreground text-[0.7rem] tracking-wide uppercase">{g.label}</span>
              <span className={cn("text-xs font-medium", g.ok ? "text-buy" : "text-warning")}>{g.detail}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ManualLaunch({
  armed,
  ready,
  pending,
  onStart,
}: {
  armed: boolean;
  ready: boolean;
  pending: boolean;
  onStart: (bias: Bias) => Promise<void>;
}) {
  return (
    <div className={cn("flex flex-col gap-3", armed && "opacity-70")}>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-foreground flex items-center gap-2 text-sm font-semibold tracking-wide">
          Lancement manuel
        </h2>
        <span className="text-muted-foreground text-xs">tu choisis le sens</span>
      </div>

      {armed && (
        <div
          className="border-border text-muted-foreground flex items-center gap-2 rounded-lg border px-3 py-2 text-xs"
          role="status"
          data-testid="manual-locked"
        >
          <Lock className="size-3.5 shrink-0" aria-hidden />
          Lancement manuel verrouillé tant qu'<strong className="text-accent-neon">AUTO est actif</strong>. Coupe AUTO
          pour tirer à la main.
        </div>
      )}

      <BiasSelector onStart={onStart} pending={pending} disabled={armed || !ready} />
    </div>
  );
}
